import {createApi} from '@reduxjs/toolkit/query/react';
import {customAxiosBaseQuery} from '../API/customAxiosBaseQuery';
import {BaseResponseType, ResponseProductListType} from '../types/TypesStore';
import {orderAPI, storeOrderAPI} from '../API/api';
import {
	DeferredSaveType,
	OrderConfirmDataType,
	OrderViewType,
	RequestOrdersDataType,
	ResponseOrdersActivitiesType,
	ResponseOrdersDataType,
	UpdateOrderDataType,
} from '../types/TypesUserOrders';
import {Depot} from '../types/CreateInvoiceTypes';
import {ProductInDepotType} from '../types/ProductSliceTypes';
import {createPaginationConfig} from './utils/paginationHelpers';

export const userOrdersApi = createApi({
	reducerPath: 'userOrdersApi',
	baseQuery: customAxiosBaseQuery,
	tagTypes: [
		'orders-list',
		'orders-list-store',
		'orders-list-activity',
		'orderDeferred',
		'orderInfo',
		'depots-list',
		'store-products',
	],
	endpoints: (builder) => ({
		ordersList: builder.query<ResponseOrdersDataType, RequestOrdersDataType>({
			query: (data) => ({
				func: orderAPI.getUserOrders,
				args: [data],
			}),
			...createPaginationConfig<RequestOrdersDataType, ResponseOrdersDataType>(
				'orders',
				[
					{name: 'count_orders', isDecimal: false},
					{name: 'sum_points', isDecimal: true},
					{name: 'sum_money', isDecimal: true},
				],
			),
			providesTags: ['orders-list'],
		}),
		ordersListStore: builder.query<
			ResponseOrdersDataType,
			RequestOrdersDataType
		>({
			query: (data) => ({
				func: orderAPI.getUserOrderStoreList,
				args: [data],
			}),
			...createPaginationConfig<RequestOrdersDataType, ResponseOrdersDataType>(
				'orders',
				[
					{name: 'count_orders', isDecimal: false},
					{name: 'sum_points', isDecimal: true},
					{name: 'sum_money', isDecimal: true},
				],
			),
			providesTags: ['orders-list-store'],
		}),

		orderActivityList: builder.query<
			ResponseOrdersActivitiesType,
			RequestOrdersDataType
		>({
			query: (data) => ({
				func: orderAPI.getUserOrderActivityList,
				args: [data],
			}),
			...createPaginationConfig<
				RequestOrdersDataType,
				ResponseOrdersActivitiesType
			>('activities', []),
			providesTags: ['orders-list-activity'],
		}),
		orderInfo: builder.query<BaseResponseType & OrderViewType, number>({
			query: (id) => ({
				func: orderAPI.getOrderInfo,
				args: [id],
			}),
			providesTags: ['orderInfo'],
		}),
		deleteUserOrder: builder.mutation<BaseResponseType & {id: number}, number>({
			query: (order_id) => ({
				func: orderAPI.getDeleteUserOrder,
				args: [order_id],
			}),
			invalidatesTags: [
				'orders-list',
				'orders-list-store',
				'orders-list-activity',
			],
		}),

		deferredInfo: builder.query<
			BaseResponseType & {user_deferred: boolean; balance: string},
			void
		>({
			query: () => ({
				func: orderAPI.getDeferredInfo,
				args: [],
			}),
			providesTags: ['orderDeferred'],
		}),
		saveDeferred: builder.mutation<BaseResponseType, DeferredSaveType>({
			query: (data) => ({
				func: orderAPI.getDeferredSave,
				args: [data],
			}),
			invalidatesTags: ['orderDeferred', 'orders-list-activity'],
		}),
		updateOrder: builder.mutation<BaseResponseType, UpdateOrderDataType>({
			query: (data) => ({
				func: orderAPI.updateOrder,
				args: [data],
			}),
			invalidatesTags: [
				'orders-list',
				'orders-list-store',
				'orders-list-activity',
				'orderInfo',
			],
		}),
		orderConfirm: builder.mutation<
			BaseResponseType & {
				href?: string;
				bill_amount?: string;
				description?: string;
				exp_time?: string;
				failure_url?: string;
				lang?: string;
				payee_id?: string;
				order_id?: string;
				success_url?: string;
			},
			OrderConfirmDataType
		>({
			query: (data) => ({
				func: orderAPI.getOrderConfirm,
				args: [data],
			}),
			invalidatesTags: [
				'orders-list',
				'orders-list-store',
				'orders-list-activity',
				'orderInfo',
			],
		}),
		
		// Depot endpoints
		getDepotsList: builder.query<Depot[], string>({
			query: (type_order) => ({
				func: storeOrderAPI.getStoreList,
				args: [type_order],
			}),
			providesTags: ['depots-list'],
		}),
		
		getStoreProductList: builder.query<ResponseProductListType, void>({
			query: () => ({
				func: storeOrderAPI.getStoreProductList,
				args: [],
			}),
			providesTags: ['store-products'],
		}),
		
		// Prepared products management
		setPreparedProducts: builder.mutation<void, ProductInDepotType[]>({
			queryFn: async (products) => {
				// This is a local state mutation, no API call needed
				return {data: undefined};
			},
			invalidatesTags: ['store-products'],
		}),
	}),
});

export const {
	useOrdersListQuery,
	useDeferredInfoQuery,
	useSaveDeferredMutation,
	useDeleteUserOrderMutation,
	useLazyOrderInfoQuery,
	useOrdersListStoreQuery,
	useOrderActivityListQuery,
	useUpdateOrderMutation,
	useOrderConfirmMutation,
	useGetDepotsListQuery,
	useGetStoreProductListQuery,
	useSetPreparedProductsMutation,
} = userOrdersApi;
